// Live OA-percentage tijdens het opmaken van een calculatie (SPEC_wijzigingen 2.5): het
// percentage van dit project en, per gelinkt project, het effect ("P-A gaat van 25,00% naar
// 30,00%"). De server levert de kost van alle andere calculaties; de bijdrage van deze
// (nog niet opgeslagen) calculatie telt de client er zelf bij.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getekendBedrag,
  isEigenProject,
  normalizePNummer,
  oaPercentage,
  projectSleutel,
  type ExtraRegel,
} from '@recotex/shared';
import { api } from '../lib/api';
import { arbeidskostTone, fmtEUR } from '../lib/format';

/** Een sprong naar 40% is een signaal dat er iets fout zit (SPEC_workflow 8.3). */
export const PCT_SIGNAAL = 40;

export interface PercentageEffect {
  sleutel: string;
  label: string;
  van: number | null;
  naar: number | null;
  noemer: number | null;
  basis: 'gefactureerd' | 'offertewaarde' | null;
  voorlopig: boolean;
}

export function useLivePercentages(opts: {
  calcId?: string;
  projectNummer: string;
  werkpostenEnMeerwerk: number;
  extraRegels: ExtraRegel[];
  /** Noemer van deze calculatie zelf, als het project nog geen andere calculatie heeft. */
  eigenNoemer: { noemer: number | null; basis: 'gefactureerd' | 'offertewaarde' | null };
  /** Is deze calculatie al naar de onderaannemer verstuurd? */
  verstuurd?: boolean;
}): { eigen: PercentageEffect; gelinkt: PercentageEffect[] } {
  const { calcId, projectNummer, werkpostenEnMeerwerk, extraRegels, eigenNoemer, verstuurd } = opts;
  const eigenSleutel = projectSleutel(projectNummer, calcId ?? 'nieuw');
  const gelinkteSleutels = useMemo(
    () => [...new Set(extraRegels.filter((r) => !isEigenProject(r, projectNummer)).map((r) => normalizePNummer(r.toegerekendProject)).filter(Boolean))],
    [extraRegels, projectNummer],
  );
  const sleutels = [...(eigenSleutel === 'calc:nieuw' ? [] : [eigenSleutel]), ...gelinkteSleutels.filter((s) => s !== eigenSleutel)];
  const key = sleutels.join(',');

  const basisQ = useQuery({
    queryKey: ['pct-basis', calcId ?? 'nieuw', key],
    queryFn: () => api.projects.percentage(sleutels, calcId),
    enabled: sleutels.length > 0,
    staleTime: 30_000,
  });
  const huidigQ = useQuery({
    queryKey: ['pct-huidig', key],
    queryFn: () => api.projects.percentage(sleutels),
    enabled: sleutels.length > 0 && !!calcId,
    staleTime: 30_000,
  });
  const basis = basisQ.data?.percentages ?? {};
  const huidig = huidigQ.data?.percentages ?? {};

  const bijdrage = (s: string) => {
    let b = s === eigenSleutel ? werkpostenEnMeerwerk : 0;
    for (const r of extraRegels) {
      if (r.teltMeeInPercentage !== true) continue;
      const doel = isEigenProject(r, projectNummer) ? eigenSleutel : normalizePNummer(r.toegerekendProject);
      if (doel === s) b += getekendBedrag(r);
    }
    return b;
  };

  const effect = (s: string, label: string): PercentageEffect => {
    const bp = basis[s];
    const noemer = bp?.noemer ?? (s === eigenSleutel ? eigenNoemer.noemer : null);
    const naar = oaPercentage((bp?.kost.kost ?? 0) + bijdrage(s), noemer);
    const van = calcId ? (huidig[s]?.pct ?? null) : (bp?.pct ?? null);
    return {
      sleutel: s,
      label,
      van,
      naar,
      noemer,
      basis: bp?.basis ?? (s === eigenSleutel ? eigenNoemer.basis : null),
      // Voorlopig als een andere (niet verstuurde) calculatie meetelt, of — voor een gelinkt project —
      // zolang déze calculatie zelf nog niet verstuurd is en er een meetellende regel naartoe gaat.
      voorlopig: !!bp?.voorlopig || (s !== eigenSleutel && !verstuurd && bijdrage(s) !== 0),
    };
  };

  return {
    eigen: effect(eigenSleutel, normalizePNummer(projectNummer) || 'dit project'),
    gelinkt: gelinkteSleutels.filter((s) => s !== eigenSleutel).map((s) => effect(s, s)),
  };
}

export function PctWaarde({ pct, voorlopig, className = '' }: { pct: number | null; voorlopig?: boolean; className?: string }) {
  if (pct == null) return <span className={`text-secondary ${className}`}>—</span>;
  return (
    <span
      className={`tabular-nums ${arbeidskostTone(pct)} ${voorlopig ? 'italic underline decoration-dotted underline-offset-4' : ''} ${className}`}
      title={voorlopig ? 'Voorlopig: bevat een regel uit een calculatie die nog niet verstuurd is' : undefined}
    >
      {pct.toFixed(2).replace('.', ',')}%{pct >= PCT_SIGNAAL && <span aria-label="signaal"> ⚠</span>}
    </span>
  );
}

/** Effect op de projecten waaraan deze calculatie regels toerekent. */
export function GelinkteProjectenEffect({ effecten }: { effecten: PercentageEffect[] }) {
  if (!effecten.length) return null;
  return (
    <div className="card mb-5 normal-case tracking-normal">
      <h3 className="text-base font-bold mb-1">Effect op gelinkte projecten</h3>
      <p className="text-xs text-secondary mb-2">
        Regels die aan een ander project toegerekend zijn, bewegen het percentage van dát project — niet van dit project. De calculaties van
        die projecten blijven onaangeroerd.
      </p>
      <ul className="space-y-1 text-sm">
        {effecten.map((e) => (
          <li key={e.sleutel} className="flex items-center gap-2 flex-wrap">
            <a href={`/projectnummer/${encodeURIComponent(e.sleutel)}`} className="font-bold text-primary underline">{e.label}</a>
            <span>gaat van</span>
            <PctWaarde pct={e.van} />
            <span>naar</span>
            <PctWaarde pct={e.naar} voorlopig={e.voorlopig} className="font-bold" />
            {e.noemer == null && <span className="text-xs text-secondary">(geen referentie bekend in de tool)</span>}
            {e.noemer != null && <span className="text-xs text-secondary">t.o.v. {e.basis} {fmtEUR(e.noemer)}</span>}
            {e.naar != null && e.naar >= PCT_SIGNAAL && <span className="text-xs text-danger">Controleer: een percentage van {PCT_SIGNAAL}% of meer wijst op een fout.</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
